import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { customGetAPI } from "services/utils/CustomAPI";
import { Box } from "@material-ui/core";
import { defaultAllSelection, themes } from "services/constants";
import { setGlobalSearchValue } from "services/Redux/userToken";
import { FilterProvider } from "../../FilterContext";
import * as Mui from "@mui/material";
import { designType, designColors } from "services/constants/designTokens";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { useLocation, useHistory } from "react-router-dom";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { setAttendanceDate } from "services/Redux/TeamZone";
import { AttendanceStatus } from "./AttendanceStatus";
import { AttendanceTimeLine } from "./AttendanceTimeLine";
import { Reports } from "./Reports";
import { checkViewerPermission } from "services/utils/HelperFunction";

export const Attendance = () => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const { attendanceDate, attendanceShiftId } = useSelector((state) => state.teamZoneReducer);
  const [departmentApi, setDepartmentApi] = useState(true);
  const [deviceOffine, setDeviceOffline] = useState(false);
  const [shiftData, setShiftData] = useState([]);
  const [leavePage, setLeavePage] = useState(0);
  const [reloadApi, setReloadApi] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState("Overview");
  const location = useLocation();
  const history = useHistory();
  // Moved up from further down this component so the URL-driven date effect
  // below (right after urlDrivenFilters) can dispatch with it — this hook
  // itself has no dependencies, so moving it earlier changes nothing else.
  const dispatch = useDispatch();
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(String(defaultAllSelection));
  const lastLoadedFilters = useRef(null);
  const { result: allDepartmentDetails } = useFetch(
    `${domain}get-department/?user_id=${userDetails?.id}&company_id=${requestCompanyId}`,
  );

  const attendanceDetails = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Attendance",
  );

  // The "Attendance" module_details entry used to carry two combined
  // submodules -- "Status" (backing Overview/Employee View/Department
  // View/Shift View together) and "Time Line" (backing Timeline View) --
  // until a live payload confirmed the backend split "Status" into four
  // standalone submodules with their own real names and permissions:
  // "Employee View", "Department View", "Shift View", "Timeline View".
  // Neither "Status" nor "Time Line" appear in that payload at all, so the
  // old lookups always failed to match, and every one of these tabs
  // (including the group that used to share "Status" access) disappeared
  // together. "Overview" still has no submodule of its own -- it renders
  // the exact same component as Employee View, just without a subModuleId
  // -- so it's shown whenever any of the three Status-descended tabs is.
  const subModuleIdByTab = {
    Overview: "Employee View",
    "Employee View": "Employee View",
    "Department View": "Department View",
    "Shift View": "Shift View",
  };
  const subModuleId =
    attendanceDetails?.submodules?.find(
      (sub) =>
        sub.sub_module_name ===
        (subModuleIdByTab[selectedComponent] || "Employee View"),
    )?.sub_module_id ?? 0;

  // The tab bar used to be a fixed list regardless of any submodule's
  // viewer permission — same "presence isn't access" gap already fixed for
  // TeamZone Approve/Reports. Until access data has loaded (module_details
  // not yet populated), everything stays true so the tab bar doesn't flash
  // empty on first render.
  const isAttendanceAccessLoaded = Array.isArray(attendanceDetails?.submodules);
  const hasEmployeeViewAccess =
    !isAttendanceAccessLoaded ||
    checkViewerPermission(userScreenAccess?.module_details, "Attendance", "Employee View");
  const hasDepartmentViewAccess =
    !isAttendanceAccessLoaded ||
    checkViewerPermission(userScreenAccess?.module_details, "Attendance", "Department View");
  const hasShiftViewAccess =
    !isAttendanceAccessLoaded ||
    checkViewerPermission(userScreenAccess?.module_details, "Attendance", "Shift View");
  const hasTimeLineAccess =
    !isAttendanceAccessLoaded ||
    checkViewerPermission(userScreenAccess?.module_details, "Attendance", "Timeline View");
  const hasStatusAccess =
    hasEmployeeViewAccess || hasDepartmentViewAccess || hasShiftViewAccess;

  const departmentDetails = [
    {
      id: defaultAllSelection,
      department_name: "All Department",
    },
    ...allDepartmentDetails,
  ];
  const { result: colorCode } = useFetch(`${domain}attendance/status-colors/`);

  const urlDrivenFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const component = (params.get("component") || "").toLowerCase();
    const attendance = (params.get("attendance") || "").toLowerCase();
    const presentStatus = (params.get("presentStatus") || "").toLowerCase();
    const overtimeStatus = (params.get("overtimeStatus") || "").toLowerCase();
    const workforce = (params.get("workforce") || "").toLowerCase();
    // Team Workspace Home passes the date it currently has selected (see
    // TeamZone/Home/index.js's navigateToModule) so landing here from Home
    // shows that same day instead of resetting to today.
    const rawDate = params.get("date") || "";
    const urlDate = moment(rawDate, "YYYY-MM-DD", true).isValid()
      ? rawDate
      : "";

    const componentMap = {
      overview: "Overview",
      "employee-view": "Employee View",
      employeeview: "Employee View",
      employee: "Employee View",
      "department-view": "Department View",
      departmentview: "Department View",
      department: "Department View",
      "shift-view": "Shift View",
      shiftview: "Shift View",
      shift: "Shift View",
      "timeline-view": "Timeline View",
      timelineview: "Timeline View",
      timeline: "Timeline View",
    };

    const attendanceMap = {
      present: "Present",
      absent: "Absent",
      overtime: "Overtime",
      onduty: "On Duty",
      "on-duty": "On Duty",
      permission: "Permission",
    };

    const presentStatusMap = {
      active: "Active",
      break: "Break",
      out: "Out",
      missing: "Missing",
      late: "Late",
      early: "Early",
      only_ot: "Only OT",
      "only-ot": "Only OT",
    };

    const overtimeStatusMap = {
      approved_ot: "Approved OT",
      "approved-ot": "Approved OT",
      pending_ot: "Pending OT",
      "pending-ot": "Pending OT",
      rejected_ot: "Rejected OT",
      "rejected-ot": "Rejected OT",
    };

    const workforceMap = {
      total: "Total",
      planned: "Planned",
      weekoff: "Week Off",
      "week-off": "Week Off",
      leave: "Leave",
    };

    const normalizedAttendance = attendanceMap[attendance] || "";
    const normalizedPresentStatus = presentStatusMap[presentStatus] || "";
    const normalizedOvertimeStatus = overtimeStatusMap[overtimeStatus] || "";
    const resolvedComponent =
      componentMap[component] ||
      (normalizedAttendance || normalizedPresentStatus || normalizedOvertimeStatus
        ? "Employee View"
        : "Overview");

    return {
      component: resolvedComponent,
      workForceSelection: workforceMap[workforce] || "Total",
      attendanceSelection: normalizedAttendance,
      presentStatusSelection: normalizedPresentStatus,
      overtimeStatusSelection: normalizedOvertimeStatus,
      date: urlDate,
    };
  }, [location.search]);

  useEffect(() => {
    setSelectedComponent(urlDrivenFilters.component);
  }, [urlDrivenFilters.component]);

  // attendanceDate otherwise persists across visits (it's backed by
  // localStorage, see services/Redux/TeamZone.js) — without this, arriving
  // here from Home with a specific day selected would still show whatever
  // date this screen was last left on. Same dispatch + localStorage write
  // AttendanceStatus's own date picker does on a manual change, so nothing
  // downstream can tell the two apart.
  useEffect(() => {
    if (!urlDrivenFilters.date) return;
    dispatch(setAttendanceDate(urlDrivenFilters.date));
    localStorage.setItem("attendanceDate", urlDrivenFilters.date);
  }, [urlDrivenFilters.date, dispatch]);

  useEffect(() => {
    const fetchShiftDetailsDetails = async () => {
      const allShifts = {
        id: defaultAllSelection,
        label: "All Shifts",
        name: "All Shifts",
      };
      const res = await customGetAPI(
        `${domain}shift-filter/?department_id=${defaultAllSelection}&user_id=${userDetails?.id}&date=${attendanceDate}&company_id=${requestCompanyId}`,
      );
      if (res.ok) {
        let data =
          res?.responseDetails?.length !== 0
            ? [allShifts, ...res?.responseDetails]
            : [allShifts];
        setShiftData(data);
      } else {
        setShiftData(allShifts);
      }
    };
    if (attendanceDate !== "") {
      fetchShiftDetailsDetails();
    }
  }, [attendanceDate, domain, userDetails?.id, requestCompanyId]);

  const filtersChanged = useCallback(() => {
    const current = {
      date: attendanceDate,
      department: selectedDepartmentId,
      shift: attendanceShiftId,
    };
    if (!lastLoadedFilters.current) return true;
    return (
      lastLoadedFilters.current.date !== current.date ||
      lastLoadedFilters.current.department !== current.department ||
      lastLoadedFilters.current.shift !== current.shift
    );
  }, [attendanceDate, attendanceShiftId, selectedDepartmentId]);

  const markFiltersLoaded = useCallback(() => {
    lastLoadedFilters.current = {
      date: attendanceDate,
      department: selectedDepartmentId,
      shift: attendanceShiftId,
    };
  }, [attendanceDate, attendanceShiftId, selectedDepartmentId]);

  const renderComponent = () => {
    switch (selectedComponent) {
      case "Employee View":
      case "Overview":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={
              selectedComponent === "Employee View" ? subModuleId : null
            }
          >
            <AttendanceStatus
              setDeviceOffline={setDeviceOffline}
              deviceOffine={deviceOffine}
              departmentDetails={departmentDetails}
              shiftData={shiftData}
              selectedComponent={selectedComponent}
              colorCode={colorCode}
              setSelectedComponent={setSelectedComponent}
              setDepartmentApi={setDepartmentApi}
              departmentApi={departmentApi}
              reloadApi={reloadApi}
              setReloadApi={setReloadApi}
              setSelectedDepartmentId={setSelectedDepartmentId}
              markFiltersLoaded={markFiltersLoaded}
              urlDrivenFilters={urlDrivenFilters}
            />
          </FilterProvider>
        );
      case "Timeline View":
        return (
          <AttendanceTimeLine
            leavePage={leavePage}
            setLeavePage={setLeavePage}
            colorCode={colorCode}
            departmentDetails={departmentDetails}
          />
        );
      case "Department View":
      case "Shift View":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <Reports
              selectedComponent={selectedComponent}
              shiftData={shiftData}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );
      default:
        break;
    }
  };
  const tabs = [
    ...(hasStatusAccess ? ["Overview"] : []),
    ...(hasEmployeeViewAccess ? ["Employee View"] : []),
    ...(hasDepartmentViewAccess ? ["Department View"] : []),
    ...(hasShiftViewAccess ? ["Shift View"] : []),
    ...(hasTimeLineAccess ? ["Timeline View"] : []),
  ];

  // If access finishes loading and the previously-selected tab is no
  // longer in the list (e.g. this role has viewer:false for "Employee
  // View"), fall back to whichever tab is actually accessible instead of
  // leaving selectedComponent pointed at a hidden tab, which would render
  // nothing (renderComponent's switch falls through to its default: break).
  useEffect(() => {
    if (!isAttendanceAccessLoaded) return;
    if (tabs.includes(selectedComponent)) return;
    if (tabs.length > 0) setSelectedComponent(tabs[0]);
    // tabs itself isn't a dependency — it's a fresh array every render, but
    // its contents are fully determined by the four hasXAccess flags (all
    // already listed below), so those are what actually need to change to
    // re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAttendanceAccessLoaded,
    hasEmployeeViewAccess,
    hasDepartmentViewAccess,
    hasShiftViewAccess,
    hasTimeLineAccess,
    selectedComponent,
  ]);

  const selectedDate = moment(attendanceDate, "YYYY-MM-DD", true);

  const formatted = (selectedDate.isValid() ? selectedDate.toDate() : new Date()).toLocaleDateString("en-US", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <Mui.Box
      sx={{
        backgroundColor: "#f5f5fd",
        minHeight: "100vh",
      }}
    >
      {/* This whole module (Attendance/AttendanceTimeLine/Reports) still
          uses the old GUI, so it must keep the app's default font — do not
          wrap any part of this screen in withDesignFont's Poppins theme
          until the module actually gets redesigned. */}
      <Mui.Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 2 }}>
        <GlobalBreadcrumb
          sx={{ mb: 1 }}
          trail={[
            {
              label: "Team Workspace",
              onClick: () => history.push("/admin/teamzone/home"),
            },
            "Attendance",
          ]}
        />
      </Mui.Box>
      <Mui.Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 1 }}>
        <Mui.Typography sx={{ ...designType.pageTitle, mb: 0.5 }}>
          Attendance
        </Mui.Typography>
        <Mui.Typography
          sx={{ ...designType.helperText, fontSize: "16px", fontWeight: 500 }}
        >
          {formatted}
        </Mui.Typography>
      </Mui.Box>
      <Mui.Grid
        container
        spacing={2}
        alignItems="center"
        sx={{
          px: { xs: 1, sm: 1.5 },
          paddingTop: { xs: 1.5, sm: 3 },
          flexWrap: "wrap",
        }}
      >
        <Mui.Box sx={{ width: "100%" }}>
          <Mui.Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: { xs: 0.75, sm: 2 },
                ml: { xs: 0, sm: 2, md: 5 },
                width: "100%",
                minWidth: 0,
                flexWrap: { xs: "wrap", sm: "nowrap" },
                overflowX: { xs: "visible", sm: "auto" },
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {tabs.map((tab) => (
                <Box
                  key={tab}
                  sx={{
                    px: { xs: 1, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                    flex: { xs: "1 1 calc(50% - 6px)", sm: "0 0 auto" },
                    minWidth: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    fontSize: { xs: 13, sm: "inherit" },
                    backgroundColor:
                      selectedComponent === tab ? "white" : "transparent",
                    color:
                      selectedComponent === tab
                        ? "#16a34a"
                        : designColors.slate600,
                    fontWeight: 600,
                    cursor: "pointer",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                    marginBottom: "-1px",
                    "&:hover": {
                      backgroundColor:
                        selectedComponent === tab ? "white" : "transparent",
                    },
                    borderBottom:
                      selectedComponent === tab
                        ? `3px solid ${themes.primaryIndicator}`
                        : "2px solid transparent",
                  }}
                  onClick={() => {
                    if (filtersChanged()) {
                      setReloadApi(true);
                      setDepartmentApi(true);
                    }
                    dispatch(setGlobalSearchValue(""));
                    setSelectedComponent(tab);
                  }}
                >
                  {tab}
                </Box>
              ))}
            </Box>
          </Mui.Box>
        </Mui.Box>
      </Mui.Grid>
      <Mui.Box
        sx={{
          paddingLeft: { xs: 1, sm: 2 },
          paddingRight: { xs: 1, sm: 2 },
          paddingTop: 0,
          minWidth: 0,
        }}
      >
        <Mui.Paper
          sx={{
            width: "100%",
            minHeight: "calc(100vh - 170px)",
            overflow: "hidden",
          }}
        >
          {renderComponent()}
        </Mui.Paper>
      </Mui.Box>
    </Mui.Box>
  );
};
